import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2, CheckCircle2, AlertCircle, Globe, Cpu, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const [crm, setCrm] = useState(null);
  const [ml, setMl] = useState(null);

  useEffect(() => {
    api.get("/integrations/crm/status").then(({ data }) => setCrm(data));
    api.get("/ml/status").then(({ data }) => setMl(data));
  }, []);

  return (
    <div className="p-6" data-testid="settings-page">
      <header className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">IQES · IQ-APP</div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">{t("set.title")}</h1>
      </header>

      <section className="bg-white border border-slate-200 rounded-xl p-5 mb-4 max-w-2xl" data-testid="ml-status-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-[#EA580C]/10 text-[#EA580C] flex items-center justify-center">
            <Cpu size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">YOLO Detection Pipeline</h2>
            <p className="text-sm text-slate-600">Object detection mode & model status</p>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-y-3 text-sm">
          <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">Active mode</dt>
          <dd className="col-span-2" data-testid="ml-active-mode">
            {ml?.active_mode === "custom" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-green-50 text-green-800 border border-green-200">
                <CheckCircle2 size={12} /> custom · trained
              </span>
            ) : ml?.active_mode === "pretrained_coco" ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-[#003366]/10 text-[#003366] border border-[#003366]/30">
                <AlertCircle size={12} /> pretrained · coco
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-[#F59E0B]/20 text-amber-900 border border-[#F59E0B]">
                <AlertTriangle size={12} /> mock · demo
              </span>
            )}
          </dd>
          <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">Weights file</dt>
          <dd className="col-span-2 font-mono text-xs text-slate-900 truncate" data-testid="ml-weights">
            {ml?.custom_weights_path || <span className="text-slate-500">— none in {ml?.weights_dir}</span>}
          </dd>
          <dt className="text-xs font-mono uppercase tracking-wider text-slate-500">Classes</dt>
          <dd className="col-span-2 font-mono text-xs text-slate-700">
            {ml?.class_names?.join(", ")}
          </dd>
        </dl>
        <p className="text-xs text-slate-500 mt-4 font-mono border-t border-slate-200 pt-3">
          To activate custom detection, drop a trained <code className="bg-slate-100 px-1">electrical_lowvoltage.pt</code> into
          {" "}<code className="bg-slate-100 px-1">{ml?.weights_dir || "/app/backend/ml/weights"}</code> and restart the backend.
          Training scripts live in <code className="bg-slate-100 px-1">/app/backend/ml/scripts/</code>.
        </p>
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5 mb-4 max-w-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-[#003366]/10 text-[#003366] flex items-center justify-center">
            <Link2 size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{t("set.crm")}</h2>
            <p className="text-sm text-slate-600">IQES CRM ecosystem integration</p>
          </div>
        </div>
        <dl className="grid grid-cols-3 gap-y-3 text-sm">
          <dt className="text-xs font-mono uppercase tracking-wider text-slate-500 col-span-1">{t("set.crm_url")}</dt>
          <dd className="col-span-2 font-mono text-slate-900 truncate" data-testid="crm-url">{crm?.crm_url || "—"}</dd>
          <dt className="text-xs font-mono uppercase tracking-wider text-slate-500 col-span-1">{t("set.crm_status")}</dt>
          <dd className="col-span-2" data-testid="crm-status">
            {crm?.linked ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-green-50 text-green-800 border border-green-200">
                <CheckCircle2 size={12} /> linked
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-xl bg-[#F59E0B]/20 text-amber-900 border border-[#F59E0B]">
                <AlertCircle size={12} /> pending
              </span>
            )}
          </dd>
        </dl>
        {crm?.note && <p className="text-xs text-slate-500 mt-3 font-mono">{crm.note}</p>}
      </section>

      <section className="bg-white border border-slate-200 rounded-xl p-5 max-w-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
            <Globe size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{t("set.language")}</h2>
            <p className="text-sm text-slate-600">Interface language</p>
          </div>
        </div>
        <div className="inline-flex gap-2">
          {[
            { code: "en", label: "English" },
            { code: "es", label: "Español" },
          ].map((l) => (
            <button
              key={l.code}
              data-testid={`lang-${l.code}-btn`}
              onClick={() => i18n.changeLanguage(l.code)}
              className={`px-4 py-2 rounded-xl text-sm font-mono uppercase tracking-wider border transition-colors ${
                i18n.language?.startsWith(l.code)
                  ? "bg-[#003366] text-white border-[#003366]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
